import type { FormEvent } from 'react';
import type { BarDuration } from '@einhaus/project314-types';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import FormInput from '@/components/UI/FormInput';
import FormSelect from '@/components/UI/FormSelect';
import { AUDIT_DURATION_OPTIONS, AUDIT_PRESETS } from './constants';
import type { AuditPreset } from './types';

interface AuditFiltersCardProps {
    formKey: string;
    onApplyFilters: (event: FormEvent<HTMLFormElement>) => void;
    onClearFocus: () => void;
    onPresetSelect: (preset: AuditPreset) => void;
    resolvedDuration: BarDuration;
    resolvedEndDateTime: string;
    resolvedFocusDateTime: string | null;
    resolvedShouldRemoveAfterHours: boolean;
    resolvedSymbol: string;
    resolvedWindowDays: number;
}

const AuditFiltersCard = ({
    formKey,
    onApplyFilters,
    onClearFocus,
    onPresetSelect,
    resolvedDuration,
    resolvedEndDateTime,
    resolvedFocusDateTime,
    resolvedShouldRemoveAfterHours,
    resolvedSymbol,
    resolvedWindowDays
}: AuditFiltersCardProps) => (
    <Card bodyClassName="space-y-5" subtitle="Manual review surface for OBV state, events, and daily screener context." title="OBV Audit">
        <form className="space-y-4" key={formKey} onSubmit={onApplyFilters}>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                <FormInput
                    noMargin
                    defaultValue={resolvedSymbol}
                    helpText="Exact asset symbol"
                    id="obv-audit-symbol"
                    label="Symbol"
                    name="symbol"
                />
                <FormSelect
                    noMargin
                    defaultValue={resolvedDuration}
                    id="obv-audit-duration"
                    label="Duration"
                    name="duration"
                    options={AUDIT_DURATION_OPTIONS.map((duration) => ({ label: duration, value: duration }))}
                />
                <FormInput
                    noMargin
                    defaultValue={resolvedEndDateTime}
                    helpText="Latest included state"
                    id="obv-audit-end-date-time"
                    label="End DateTime"
                    name="endDateTime"
                />
                <FormInput
                    noMargin
                    defaultValue={resolvedFocusDateTime ?? ''}
                    helpText="Optional exact bar datetime"
                    id="obv-audit-focus-date-time"
                    label="Focus DateTime"
                    name="focusDateTime"
                />
                <FormInput
                    noMargin
                    defaultValue={String(resolvedWindowDays)}
                    helpText="Audit history requested from backend"
                    id="obv-audit-window-days"
                    label="Window Days"
                    name="windowDays"
                />
                <label className="flex items-end gap-3 rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-3 text-sm text-zinc-300">
                    <input defaultChecked={resolvedShouldRemoveAfterHours} name="shouldRemoveAfterHours" type="checkbox" />
                    Remove After Hours
                </label>
            </div>

            <div className="flex flex-wrap gap-3">
                <Button type="submit" variant="dark">
                    Run Audit
                </Button>
                {resolvedFocusDateTime ? (
                    <Button type="button" variant="secondary" onClick={onClearFocus}>
                        Clear Focus
                    </Button>
                ) : null}
            </div>
        </form>

        <div className="flex flex-wrap gap-3">
            {AUDIT_PRESETS.map((preset) => (
                <Button key={preset.label} size="sm" variant="outline-dark" onClick={() => onPresetSelect(preset)}>
                    {preset.label}
                </Button>
            ))}
        </div>
    </Card>
);

export default AuditFiltersCard;
